/**
 * Typed client for the Phase 2 insights endpoints (packages/protocol/src/api-insights.ts).
 * Kept inside the insights feature so the shared src/api.ts stays untouched.
 */
import type {
  DwellReport,
  ErrorGroupDetail,
  Funnel,
  FunnelDef,
  FunnelResult,
  FunnelSuggestion,
  Heatmap,
  HeatmapKind,
  IssueCluster,
  IssueClusterDetail,
  IssueOccurrence,
  Overview,
  Page,
  Release,
  Severity,
  SignalKind,
  VitalsSeries,
} from "@backstory/protocol";
import { request } from "../../api";

const enc = encodeURIComponent;

export interface HeatmapRoute {
  route: string;
  sessions: number;
  errorPct?: number;
}

function qs(params: object): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
    if (v === undefined || v === null || v === "") continue;
    parts.push(`${enc(k)}=${enc(String(v))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export interface FlagValueCount {
  value: string;
  sessions: number;
}
export interface FlagStat {
  flag: string;
  values: FlagValueCount[];
}
export interface FunnelCompare {
  funnelId: string;
  flag: string;
  from: string;
  to: string;
  variants: Array<{ value: string; steps: FunnelResult["steps"] }>;
}

export interface TimeRange {
  from: string; // RFC3339
  to: string;
}

export interface LocationCountry {
  code: string;
  sessions: number;
  share: number;
}
export interface LocationPlace {
  code: string;
  region?: string;
  city?: string;
  sessions: number;
  share: number;
  lat?: number;
  lon?: number;
}
export interface LocationReport {
  from: string;
  to: string;
  sessions: number;
  located: number;
  countries: LocationCountry[];
  places: LocationPlace[];
}

export interface IssuesQuery extends Partial<TimeRange> {
  q?: string;
  kind?: SignalKind;
  severity?: Severity;
  /** A stored status, or "snoozed" for issues whose snooze has not run out. */
  status?: IssueCluster["status"] | "snoozed";
  route?: string;
  release?: string;
  /** Empty is anyone, "unassigned" is nobody, otherwise a member email. */
  assignee?: string;
  /** Empty is last seen. */
  sort?: "occurrences" | "impact" | "newest" | "severity";
  cursor?: string;
  limit?: number;
}

export interface IssuePatch {
  status?: IssueCluster["status"];
  assignee?: string | null;
  snoozedUntil?: string | null;
}

export interface HeatmapQuery extends Partial<TimeRange> {
  route: string;
  kind: HeatmapKind;
  breakpoint: Heatmap["breakpoint"];
}

export const insightsApi = {
  issues: (q: IssuesQuery) => request<Page<IssueCluster>>(`/v1/issues${qs(q)}`),
  issue: (clusterId: string) => request<IssueClusterDetail>(`/v1/issues/${enc(clusterId)}`),
  patchIssue: (clusterId: string, patch: IssuePatch) => request<IssueCluster>(`/v1/issues/${enc(clusterId)}`, { method: "PATCH", body: patch }),
  issueSessions: (clusterId: string, cursor?: string) => request<Page<IssueOccurrence>>(`/v1/issues/${enc(clusterId)}/sessions${qs({ cursor })}`),

  /** Compatibility lookup used only to redirect old fingerprint bookmarks. */
  error: (fingerprint: string) => request<ErrorGroupDetail>(`/v1/errors/${enc(fingerprint)}`),

  releases: (range?: Partial<TimeRange>) => request<Release[]>(`/v1/releases${qs(range ?? {})}`),

  heatmapRoutes: (range: TimeRange, breakpoint?: Heatmap["breakpoint"]) => request<HeatmapRoute[]>(`/v1/heatmaps/routes${qs({ ...range, breakpoint })}`),
  heatmap: (q: HeatmapQuery) => request<Heatmap>(`/v1/heatmaps${qs(q)}`),

  funnels: () => request<Funnel[]>(`/v1/funnels`),
  createFunnel: (def: FunnelDef) => request<Funnel>(`/v1/funnels`, { method: "POST", body: def }),
  patchFunnel: (id: string, def: Partial<FunnelDef>) => request<Funnel>(`/v1/funnels/${enc(id)}`, { method: "PATCH", body: def }),
  deleteFunnel: (id: string) => request<void>(`/v1/funnels/${enc(id)}`, { method: "DELETE" }),
  funnelResults: (id: string, range: TimeRange) => request<FunnelResult>(`/v1/funnels/${enc(id)}/results${qs(range)}`),
  funnelCompare: (id: string, range: TimeRange, flag: string) => request<FunnelCompare>(`/v1/funnels/${enc(id)}/compare${qs({ ...range, flag })}`),
  flags: (range: Partial<TimeRange>) => request<FlagStat[]>(`/v1/flags${qs(range)}`),
  funnelSuggestions: () => request<FunnelSuggestion[]>(`/v1/funnels/suggestions`),

  overview: (range: TimeRange) => request<Overview>(`/v1/dashboard/overview${qs(range)}`),
  locations: (range: TimeRange) => request<LocationReport>(`/v1/locations${qs(range)}`),
  dwell: (range: TimeRange) => request<DwellReport>(`/v1/dwell${qs(range)}`),
  vitals: (range: TimeRange, route?: string) => request<VitalsSeries>(`/v1/vitals${qs({ ...range, route })}`),
};

export const insightsKeys = {
  issues: (q: IssuesQuery, project?: string) => ["insights", "issues", q, project] as const,
  issue: (id: string) => ["insights", "issue", id] as const,
  issueSessions: (id: string) => ["insights", "issueSessions", id] as const,
  error: (fp: string) => ["insights", "error", fp] as const,
  releases: (range: Partial<TimeRange>, project?: string) => ["insights", "releases", range, project] as const,
  heatmapRoutes: (range: TimeRange, breakpoint: string, project?: string) => ["insights", "heatmapRoutes", range, breakpoint, project] as const,
  heatmap: (q: HeatmapQuery, project?: string) => ["insights", "heatmap", q, project] as const,
  funnels: (project?: string) => ["insights", "funnels", project] as const,
  funnelResults: (id: string, range: TimeRange) => ["insights", "funnelResults", id, range] as const,
  funnelCompare: (id: string, range: TimeRange, flag: string) => ["insights", "funnelCompare", id, range, flag] as const,
  flags: (range: Partial<TimeRange>, project?: string) => ["insights", "flags", range, project] as const,
  funnelSuggestions: (project?: string) => ["insights", "funnelSuggestions", project] as const,
  overview: (range: TimeRange, project?: string) => ["insights", "overview", range, project] as const,
  locations: (range: TimeRange, project?: string) => ["insights", "locations", range, project] as const,
  dwell: (range: TimeRange, project?: string) => ["insights", "dwell", range, project] as const,
  vitals: (range: TimeRange, route?: string, project?: string) => ["insights", "vitals", range, route, project] as const,
};
